import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { BreakdownTable } from '@/components/breakdown-table'
import { DeltaInline, DeltaPill } from '@/components/delta'
import { DetailList, PREVIEW_ROWS, useListExpansion } from '@/components/detail-list'
import { Flag } from '@/components/flag'
import { Logo } from '@/components/logo'
import { MetricCards } from '@/components/metric-cards'
import { Panel } from '@/components/panel'
import { PerformancePanel } from '@/components/performance-panel'
import { RankedList } from '@/components/ranked-list'
import { SearchConsoleStatus } from '@/components/status-dot'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { countryName } from '@/lib/countries'
import { DEFAULT_TABLE, type MetricsRow, type TableState } from '@/lib/site-data'
import { ShareOfSite } from './_app/$org/$host/-section'

/**
 * Not linked from the product. It exists so both themes can be judged side by
 * side — dark hairlines in particular only work if you can see them on a real
 * monitor.
 */
export const Route = createFileRoute('/design')({
  component: DesignPage,
})

const COLORS = [
  ['canvas', 'Page background'],
  ['card', 'Panels, tables, inputs'],
  ['border', 'Hairlines'],
  ['border-strong', 'Hover edges'],
  ['muted-foreground', 'Secondary text'],
  ['foreground', 'Primary text'],
  ['plum', 'Ring, active marker'],
  ['plum-selected', 'Selected rows'],
  ['success', 'Connected'],
  ['warning', 'Needs attention'],
  ['destructive', 'Danger'],
] as const

/**
 * A month of plausible traffic, generated rather than fetched: the chart has to
 * be judgeable on a workspace with nothing connected, which is every workspace
 * on the first day.
 */
const SAMPLE_SERIES = Array.from({ length: 28 }, (_, day) => {
  const weekend = day % 7 === 5 || day % 7 === 6
  const impressions = Math.round(
    (4200 + day * 60 + Math.sin(day / 2.4) * 900) * (weekend ? 0.62 : 1),
  )
  const clicks = Math.round(impressions * (0.031 + Math.sin(day / 3.1) * 0.006))
  return {
    date: `2026-07-${String(day + 1).padStart(2, '0')}`,
    clicks,
    impressions,
    ctr: clicks / impressions,
    position: 12.4 - day * 0.09 + Math.sin(day / 1.9) * 0.7,
  }
})

const SAMPLE_PREVIOUS = SAMPLE_SERIES.map((point, day) => ({
  ...point,
  date: `2026-06-${String(day + 1).padStart(2, '0')}`,
  clicks: Math.round(point.clicks * 0.86),
  impressions: Math.round(point.impressions * 0.91),
  ctr: point.ctr * 0.95,
  position: point.position + 1.6,
}))

const SAMPLE_TOTALS: MetricsRow = {
  clicks: SAMPLE_SERIES.reduce((sum, point) => sum + point.clicks, 0),
  impressions: SAMPLE_SERIES.reduce((sum, point) => sum + point.impressions, 0),
  ctr: 0.0324,
  position: 10.8,
  previous: {
    clicks: SAMPLE_PREVIOUS.reduce((sum, point) => sum + point.clicks, 0),
    impressions: SAMPLE_PREVIOUS.reduce((sum, point) => sum + point.impressions, 0),
    ctr: 0.0301,
    position: 12.4,
  },
  delta: { clicks: 0, impressions: 0, ctr: 0, position: -1.6 },
}

/** The same month from the second engine: a fraction of the volume, and no
 * position, because Bing reports none at site level. */
const bingOf = (points: typeof SAMPLE_SERIES) =>
  points.map((point, day) => {
    const impressions = Math.round(point.impressions * (0.22 + Math.cos(day / 4) * 0.04))
    const clicks = Math.round(impressions * 0.041)
    return { ...point, clicks, impressions, ctr: clicks / impressions, position: null }
  })

const SAMPLE_BING_SERIES = bingOf(SAMPLE_SERIES)
const SAMPLE_BING_PREVIOUS = bingOf(SAMPLE_PREVIOUS)

const bingTotal = (points: typeof SAMPLE_BING_SERIES) => {
  const clicks = points.reduce((sum, point) => sum + point.clicks, 0)
  const impressions = points.reduce((sum, point) => sum + point.impressions, 0)
  return { clicks, impressions, ctr: clicks / impressions, position: null }
}

const SAMPLE_BING = {
  totals: {
    ...bingTotal(SAMPLE_BING_SERIES),
    previous: bingTotal(SAMPLE_BING_PREVIOUS),
    delta: null,
  },
  series: SAMPLE_BING_SERIES,
  previousSeries: SAMPLE_BING_PREVIOUS,
}

const SAMPLE_COUNTRIES = ['gbr', 'usa', 'deu', 'ind', 'aus']

const SAMPLE_ROWS = [
  ['seo reporting tool', 1284, 41200, 0.031, 4.2, 1103],
  ['search console alternative', 812, 26400, 0.0308, 6.8, 940],
  ['keyword tracking software', 604, 31900, 0.0189, 11.4, 512],
  ['how to improve ctr', 388, 12750, 0.0304, 8.1, 401],
  ['rank tracker uk', 211, 9840, 0.0214, 14.7, 168],
] as const

function SampleBreakdown() {
  const [table, setTable] = useState<TableState>(DEFAULT_TABLE)

  const items = SAMPLE_ROWS.map(([label, clicks, impressions, ctr, position, before]) => ({
    key: label,
    label,
    metrics: {
      clicks,
      impressions,
      ctr,
      position,
      previous: { clicks: before, impressions, ctr: ctr * 0.94, position: position + 1.2 },
      delta: { clicks: clicks - before, impressions: 0, ctr: 0, position: -1.2 },
    },
  }))

  return (
    <BreakdownTable
      dimensionLabel="Keyword"
      items={items}
      total={SAMPLE_ROWS.length}
      table={table}
      onTableChange={setTable}
      onRowSelect={() => {}}
    />
  )
}

/** The five-row list a detail page opens with, and what it does when asked for
 *  the rest. Sampled here because both states have to be judged together. */
function SampleDetailList() {
  const expansion = useListExpansion('sample', 10)
  const rows = SAMPLE_ROWS.slice(0, expansion.expanded ? SAMPLE_ROWS.length : PREVIEW_ROWS)

  return (
    <DetailList title="Landing pages" total={42} expansion={expansion}>
      <RankedList
        dimension="Page"
        rows={rows.map(([label, clicks, impressions, ctr, position, before]) => ({
          key: label,
          label,
          mono: true,
          metrics: {
            clicks,
            impressions,
            ctr,
            position,
            previous: { clicks: before, impressions, ctr: ctr * 0.94, position: position + 1.2 },
            delta: null,
          },
        }))}
        empty="Nothing here in this window."
      />
    </DetailList>
  )
}

function DesignPage() {
  return (
    <div className="grid min-h-svh grid-cols-1 lg:grid-cols-2">
      <ThemePanel theme="light" />
      <ThemePanel theme="dark" />
    </div>
  )
}

function ThemePanel({ theme }: { theme: 'light' | 'dark' }) {
  return (
    <div className={`${theme} bg-canvas text-foreground`}>
      <div className="space-y-10 border-border px-8 py-10 lg:border-r">
        <div className="flex items-center justify-between">
          <Logo />
          <span className="font-mono text-[11px] text-muted-foreground">{theme}</span>
        </div>

        <Section title="Colour">
          <div className="grid grid-cols-2 gap-2">
            {COLORS.map(([token, description]) => (
              <div
                key={token}
                className="flex items-center gap-3 rounded-md border border-border bg-card p-2"
              >
                <span
                  className="size-7 shrink-0 rounded border border-border"
                  style={{ background: `var(--${token})` }}
                />
                <span className="min-w-0">
                  <span className="block truncate font-mono text-[11px]">{token}</span>
                  <span className="block truncate text-[11px] text-muted-foreground">
                    {description}
                  </span>
                </span>
              </div>
            ))}
          </div>
        </Section>

        <Section title="Type">
          <div className="space-y-3 rounded-lg border border-border bg-card p-5">
            <h1 className="text-2xl">Organic traffic, measured</h1>
            <h2 className="text-lg">Section heading</h2>
            <p className="text-[14px]">
              Body copy runs in Inter. It stays comfortable at paragraph length, which is why the
              interface is not entirely monospace.
            </p>
            <p className="font-mono text-[13px]">
              example.com · sc-domain:example.com · 18 Aug 2026 · 1,284,097
            </p>
            <p className="text-[13px] text-muted-foreground">
              Secondary text sits at 13px in the muted tone.
            </p>
          </div>
        </Section>

        <Section title="Controls">
          <div className="space-y-4 rounded-lg border border-border bg-card p-5">
            <div className="flex flex-wrap items-center gap-2">
              <Button size="sm">Primary</Button>
              <Button size="sm" variant="outline">
                Outline
              </Button>
              <Button size="sm" variant="ghost">
                Ghost
              </Button>
              <Button size="sm" variant="destructive">
                Delete
              </Button>
              <Button size="sm" variant="link">
                Link
              </Button>
              <Button size="sm" disabled>
                Disabled
              </Button>
            </div>
            <div className="max-w-xs space-y-2">
              <Label htmlFor={`design-input-${theme}`}>Domain</Label>
              <Input
                id={`design-input-${theme}`}
                defaultValue="example.com"
                className="font-mono"
              />
            </div>
          </div>
        </Section>

        <Section title="Status">
          <Panel title="Search Console" meta="3">
            <div className="space-y-3">
              <SearchConsoleStatus status="connected" email="tom@sharkey.im" />
              <SearchConsoleStatus status="needs-reconnect" />
              <SearchConsoleStatus status="not-connected" />
            </div>
          </Panel>
        </Section>

        <Section title="Terminal accents">
          <Panel title="Registers">
            <div className="space-y-3 text-[13px]">
              <p className="mono-label text-muted-foreground">mono-label · structure</p>
              <p className="font-mono text-[12px]">sc-domain:example.com · 18 Aug 2026</p>
              <p>Prose stays in Inter, because paragraphs are not output.</p>
              <p className="flex items-center gap-2 font-mono text-[13px]">
                <span className="text-plum">❯</span>
                <span className="caret text-muted-foreground/70" />
              </p>
            </div>
          </Panel>
        </Section>

        <Section title="Reporting">
          <PerformancePanel
            totals={SAMPLE_TOTALS}
            series={SAMPLE_SERIES}
            previousSeries={SAMPLE_PREVIOUS}
            rangeStart="2026-07-01"
            previousStart="2026-06-01"
          />
        </Section>

        {/* The same report once a second engine is connected: the lines take
            their engine's hue and plum steps aside, and each tile carries the
            split under the combined figure. */}
        <Section title="Reporting, two engines">
          <PerformancePanel
            totals={SAMPLE_TOTALS}
            series={SAMPLE_SERIES}
            previousSeries={SAMPLE_PREVIOUS}
            bing={SAMPLE_BING}
            rangeStart="2026-07-01"
            previousStart="2026-06-01"
          />
        </Section>

        {/* What a detail page opens with, where the report above opens with one
            chart and a switcher. Four small ones is the right trade when the
            subject is a single keyword and the question is which of the four
            moved. */}
        <Section title="One keyword">
          <div className="space-y-3">
            <MetricCards totals={SAMPLE_TOTALS} series={SAMPLE_SERIES} />
            <ShareOfSite part={SAMPLE_TOTALS.clicks} whole={128_400} />
          </div>
        </Section>

        <Section title="A detail page's list">
          <SampleDetailList />
        </Section>

        <Section title="Breakdown">
          <SampleBreakdown />
        </Section>

        <Section title="Change">
          <Panel title="Deltas">
            <div className="flex flex-wrap items-center gap-3">
              <DeltaPill value={0.128} />
              <DeltaPill value={-0.028} />
              <DeltaPill value={-1.6} higherIsBetter={false} absolute />
              <DeltaInline value={0.128} />
              <DeltaInline value={-0.028} />
            </div>
          </Panel>
        </Section>

        <Section title="Places">
          <Panel title="Countries" meta={`${SAMPLE_COUNTRIES.length}`}>
            <ul className="space-y-2.5 text-[13px]">
              {SAMPLE_COUNTRIES.map((code) => (
                <li key={code} className="flex items-center gap-2.5">
                  <Flag code={code} />
                  <span className="flex-1">{countryName(code)}</span>
                  <span className="font-mono text-[12px] text-muted-foreground">
                    {code.toUpperCase()}
                  </span>
                </li>
              ))}
            </ul>
          </Panel>
        </Section>

        <Section title="Table">
          <div className="overflow-hidden rounded-lg border border-border bg-card">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-border bg-secondary/40">
                  <th className="mono-label h-9 w-10 pr-0 pl-4 text-right text-muted-foreground">
                    #
                  </th>
                  <th className="mono-label h-9 px-4 text-muted-foreground">Site</th>
                  <th className="mono-label h-9 px-4 text-right text-muted-foreground">Clicks</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['example.com', '12,840'],
                  ['docs.example.com', '3,102'],
                  ['shop.example.com', '981'],
                ].map(([host, clicks], index) => (
                  <tr
                    key={host}
                    className={`border-b border-border last:border-b-0 ${index === 0 ? 'bg-plum-selected' : ''}`}
                  >
                    <td className="w-10 py-3 pr-0 pl-4 text-right font-mono text-[11px] text-muted-foreground tabular">
                      {String(index + 1).padStart(2, '0')}
                    </td>
                    <td className="px-4 py-3 font-mono text-[13px]">{host}</td>
                    <td className="px-4 py-3 text-right font-mono text-[13px] tabular">{clicks}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      </div>
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3">
      <h2 className="mono-label text-muted-foreground">{title}</h2>
      {children}
    </section>
  )
}
